import { Product } from "@/prisma-types";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2, Truck } from "lucide-react";
import React from "react";
import { toast } from "react-hot-toast";
import { Button } from "../../../components/ui/Button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../../components/ui/Card";
import { Input } from "../../../components/ui/Input";
import { Label } from "../../../components/ui/label";
import { fetchWithAuth } from "../../../hooks/useAuth";

interface FormState {
  enabled: boolean;
  minQty: number | null;
}

const ProductPromoPannel: React.FC<{ product: Product }> = ({ product }) => {
  const queryClient = useQueryClient();
  const [isEditing, setIsEditing] = React.useState(false);
  const [form, setForm] = React.useState<FormState>({
    enabled: Boolean((product as any).freeShippingEnabled),
    minQty:
      (product as any).freeShippingMinQty !== undefined
        ? (product as any).freeShippingMinQty
        : null,
  });

  React.useEffect(() => {
    setForm({
      enabled: Boolean((product as any).freeShippingEnabled),
      minQty:
        (product as any).freeShippingMinQty !== undefined
          ? (product as any).freeShippingMinQty
          : null,
    });
  }, [product]);

  const updatePromo = useMutation({
    mutationFn: async (data: FormState) => {
      const response = await fetchWithAuth(
        `/api/admin/products/${product.id}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            freeShippingEnabled: data.enabled,
            freeShippingMinQty: data.enabled
              ? data.minQty !== null
                ? Number(data.minQty)
                : null
              : null,
          }),
        }
      );
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.message || "Eroare la salvarea promoțiilor");
      }
      return response.json();
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["product", product.id.toString()],
      });
      toast.success("Setări de transport gratuit salvate");
      setIsEditing(false);
    },
    onError: (e: any) => toast.error(e?.message || "Eroare necunoscută"),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Truck className="w-5 h-5" />
          Promoții Transport
        </CardTitle>
      </CardHeader>
      <CardContent>
        {isEditing ? (
          <form
            className="space-y-6"
            onSubmit={(e) => {
              e.preventDefault();
              updatePromo.mutate({
                enabled: form.enabled,
                minQty:
                  form.enabled && form.minQty !== null
                    ? Math.max(1, Number(form.minQty) || 1)
                    : null,
              });
            }}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="flex items-center gap-3">
                <input
                  id="free-shipping-enabled"
                  type="checkbox"
                  className="h-4 w-4"
                  checked={form.enabled}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, enabled: e.target.checked }))
                  }
                />
                <Label htmlFor="free-shipping-enabled" className="mb-0">
                  Activează transport gratuit pentru acest produs
                </Label>
              </div>
              <div>
                <Label className="mb-2">
                  Cantitate minimă pentru transport gratuit
                </Label>
                <Input
                  type="number"
                  min={1}
                  step={1}
                  disabled={!form.enabled}
                  value={
                    form.minQty === null || form.minQty === undefined
                      ? ""
                      : String(form.minQty)
                  }
                  onChange={(e) => {
                    const value = e.target.value;
                    setForm((f) => ({
                      ...f,
                      minQty: value === "" ? null : Number(value),
                    }));
                  }}
                  placeholder="Ex: 2 (minim 2 bucăți)"
                />
                <div className="text-xs text-muted-foreground mt-1">
                  Se aplică dacă clientul cumpără cel puțin acest număr de
                  bucăți.
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsEditing(false)}
              >
                Anulează
              </Button>
              <Button type="submit" disabled={updatePromo.isPending}>
                {updatePromo.isPending ? (
                  <span className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Se salvează...
                  </span>
                ) : (
                  "Salvează"
                )}
              </Button>
            </div>
          </form>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <div className="text-sm font-medium text-muted-foreground mb-1">
                Transport gratuit activat
              </div>
              <div className="text-foreground">
                {form.enabled ? "Da" : "Nu"}
              </div>
            </div>
            <div>
              <div className="text-sm font-medium text-muted-foreground mb-1">
                Cantitate minimă
              </div>
              <div className="text-foreground">
                {form.enabled ? form.minQty ?? "—" : "—"}
              </div>
            </div>
            <div className="col-span-2">
              <Button onClick={() => setIsEditing(true)}>Editează</Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ProductPromoPannel;
